import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as SupplierIcon,
  DeleteOutline as ArchiveIcon,
  EditOutlined as EditIcon,
  InboxOutlined as InboxIcon,
  ListAltOutlined as TableIcon,
  MapOutlined as MapIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import CollectionPointsMap from '../../components/collection-points/CollectionPointsMap';
import BulkImportControls from '../../components/common/BulkImportControls';
import PageHeader from '../../components/PageHeader';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import StatusChip from '../../components/StatusChip';
import PaginationControl from '../../components/PaginationControl';
import SupplierFormDialog from '../../components/suppliers/SupplierFormDialog';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useCollectionPointsMapData } from '../../hooks/useCollectionPointsMapData';
import { useConfirm } from '../../hooks/useConfirm';
import { downloadSupplierImportTemplate, importSuppliers } from '../../services/supplier.service';
import { getWasteCatalogMaterialOptions, type WasteCatalogOption } from '../../services/waste-catalog.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { useSupplierStore } from '../../stores/supplier.store';
import type { Supplier, SupplierInput, SupplierStatus } from '../../types/supplier.type';

type SuppliersView = 'table' | 'map';
const PAGE_SIZE = 20;

export default function Suppliers() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    suppliers,
    total,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    archiveSupplier,
  } = useSupplierStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | Exclude<SupplierStatus, 'archived'>>('');
  const [materialId, setMaterialId] = useState('');
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);

  const orgId = selectedOrganization?.id;
  const view: SuppliersView = searchParams.get('view') === 'map' ? 'map' : 'table';
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.create`));
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.write`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.delete`));

  const mapData = useCollectionPointsMapData(orgId, view === 'map');

  useEffect(() => {
    void getWasteCatalogMaterialOptions(orgId).then(setMaterialOptions).catch(() => setMaterialOptions([]));
  }, []);

  useEffect(() => {
    if (!orgId) return;
    void fetchSuppliers({
      organization_id: orgId,
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      status: status || undefined,
      material_id: materialId || undefined,
    });
  }, [orgId, page, search, status, materialId, fetchSuppliers]);

  useEffect(() => setPage(1), [orgId, search, status, materialId]);

  const materialLabels = useMemo(
    () => new Map(materialOptions.map((option) => [option.value, option.label])),
    [materialOptions],
  );
  const hasActiveFilters = Boolean(search || status || materialId);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setMaterialId('');
  };

  if (!selectedOrganization) {
    return <Alert severity="info">{t('suppliers.details.selectOrganization')}</Alert>;
  }

  const openCreate = () => {
    clearError();
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (supplier: Supplier) => {
    clearError();
    setEditing(supplier);
    setFormOpen(true);
  };

  const saveSupplier = async (input: SupplierInput) => {
    const result = editing
      ? await updateSupplier(selectedOrganization.id, editing.id, input)
      : await createSupplier(selectedOrganization.id, input);
    return Boolean(result);
  };

  const archive = async (supplier: Supplier) => {
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('suppliers.archiveConfirm', { name: supplier.name }),
      tone: 'danger',
    });
    if (confirmed) await archiveSupplier(selectedOrganization.id, supplier.id);
  };

  return (
    <Box>
      <PageHeader
        icon={<SupplierIcon />}
        title={t('suppliers.title')}
        subtitle={t('suppliers.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={canCreate ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <BulkImportControls
              onDownloadTemplate={() => downloadSupplierImportTemplate(selectedOrganization?.id ?? '')}
              templateFilename="suppliers_import_template.xlsx"
              onImport={(file) => importSuppliers(selectedOrganization!.id, file)}
              onImported={() => orgId && void fetchSuppliers({ organization_id: orgId, page, limit: PAGE_SIZE })}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('suppliers.addSupplier')}
            </Button>
          </Stack>
        ) : undefined}
      />

      {error && !formOpen && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={view}
        onChange={(_, next: SuppliersView) => setSearchParams({ view: next }, { replace: true })}
        sx={{ mb: 2 }}
      >
        <Tab value="table" icon={<TableIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.table')} />
        <Tab value="map" icon={<MapIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.map')} />
      </Tabs>

      {view === 'map' ? (
        <CollectionPointsMap
          points={mapData.collectionPoints}
          loading={mapData.isLoading}
          materialLabels={mapData.materialLabels}
          supplierOptions={mapData.supplierOptions}
          supplierNameById={mapData.supplierNameById}
          filters={mapData.filters}
          onFiltersChange={mapData.setFilters}
          onOpenSupplier={(id) => navigate(`/suppliers/${id}`)}
        />
      ) : (
        <>
          <Card elevation={0} variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: hasActiveFilters ? '2fr 1fr 1fr auto' : '2fr 1fr 1fr',
                },
                gap: 2,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label={t('suppliers.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
              />
              <FormControl size="small">
                <InputLabel>{t('suppliers.status.label')}</InputLabel>
                <Select label={t('suppliers.status.label')} value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
                  <MenuItem value="">{t('suppliers.status.all')}</MenuItem>
                  <MenuItem value="active">{t('suppliers.status.active')}</MenuItem>
                  <MenuItem value="paused">{t('suppliers.status.paused')}</MenuItem>
                  <MenuItem value="blocked">{t('suppliers.status.blocked')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>{t('suppliers.wasteType.label')}</InputLabel>
                <Select label={t('suppliers.wasteType.label')} value={materialId} onChange={(event) => setMaterialId(event.target.value)}>
                  <MenuItem value="">{t('suppliers.wasteType.all')}</MenuItem>
                  {materialOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              {hasActiveFilters && (
                <Button size="small" onClick={clearFilters} sx={{ whiteSpace: 'nowrap' }}>{t('suppliers.clearFilters')}</Button>
              )}
            </Box>
            </CardContent>
          </Card>

          {isLoading ? <LoadingSkeleton variant="table" filters={false} columns={6} rows={6} /> : (
            <Paper elevation={2} sx={elevatedPanelSx}>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader sx={{ '& .MuiTableCell-head': { py: 1.5, px: 2 }, '& .MuiTableCell-body': { py: 1.75, px: 2 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('suppliers.table.supplier')}</TableCell>
                      <TableCell>{t('suppliers.table.primaryContact')}</TableCell>
                      <TableCell>{t('suppliers.table.wasteTypes')}</TableCell>
                      <TableCell>{t('suppliers.table.collectionPoints')}</TableCell>
                      <TableCell>{t('suppliers.table.status')}</TableCell>
                      <TableCell align="right">{t('suppliers.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map((supplier) => {
                      const primary = supplier.contacts[0];
                      return (
                        <TableRow key={supplier.id} hover sx={{ transition: 'background-color 0.2s ease' }}>
                          <TableCell>
                            <Typography fontWeight={700}>{supplier.name}</Typography>
                            {supplier.source === 'facility_linked' ? (
                              <Chip size="small" variant="outlined" color="info" label={t('suppliers.source.facilityLinked')} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {t('suppliers.table.supplierCaption')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {primary ? (
                              <>
                                <Typography variant="body2">{primary.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{primary.phone ?? primary.email}</Typography>
                              </>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                              {supplier.material_ids.slice(0, 3).map((id) => (
                                <Chip key={id} size="small" label={materialLabels.get(id) ?? id} />
                              ))}
                              {supplier.material_ids.length > 3 && <Chip size="small" label={`+${supplier.material_ids.length - 3}`} />}
                              {supplier.material_ids.length === 0 && '—'}
                            </Stack>
                          </TableCell>
                          <TableCell>{supplier.collection_points_count}</TableCell>
                          <TableCell><StatusChip value={supplier.status} /></TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('suppliers.table.view')}><IconButton aria-label={`${t('suppliers.table.view')} ${supplier.name}`} size="small" onClick={() => navigate(`/suppliers/${supplier.id}`)} sx={{ color: 'text.secondary', width: 40, height: 40 }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                            {canEdit && <Tooltip title={t('suppliers.table.edit')}><IconButton aria-label={`${t('suppliers.table.edit')} ${supplier.name}`} size="small" onClick={() => openEdit(supplier)} sx={{ color: 'primary.main', width: 40, height: 40 }}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                            {canArchive && <Tooltip title={t('suppliers.table.archive')}><IconButton aria-label={`${t('suppliers.table.archive')} ${supplier.name}`} size="small" color="error" onClick={() => void archive(supplier)} sx={{ width: 40, height: 40 }}><ArchiveIcon fontSize="small" /></IconButton></Tooltip>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {suppliers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 8, border: 0 }}>
                          <Stack alignItems="center" spacing={1.5}>
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                color: 'text.secondary',
                              }}
                            >
                              <InboxIcon fontSize="large" />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {hasActiveFilters ? t('suppliers.emptyState.filteredTitle') : t('suppliers.emptyState.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              {hasActiveFilters ? t('suppliers.emptyState.filteredHint') : t('suppliers.emptyState.hint')}
                            </Typography>
                            {!hasActiveFilters && canCreate && (
                              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                                {t('suppliers.addSupplier')}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <PaginationControl
                  page={page}
                  limit={PAGE_SIZE}
                  total={total}
                  onPageChange={setPage}
                />
              </Box>
            </Paper>
          )}
        </>
      )}

      <SupplierFormDialog
        open={formOpen}
        supplier={editing}
        saving={isSaving}
        submitError={formOpen ? error : null}
        onClose={() => setFormOpen(false)}
        onSubmit={saveSupplier}
      />
      {confirmDialog}
    </Box>
  );
}
